<template>
    <div>
        <b-card
            class="drag-handle"
            :class="{ 'merlin-asset-border': isMerlinMedia}"
            no-body
            :header-class="!worksheet_contents ? 'border-bottom-0' : ''"
        >
            <template #header>
                <CardID :id="worksheet.id" type="Printable"  />
                <span
                    v-if="isMerlinMedia"
                    class="merlin-asset-warning"
                >
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width />
                    Merlin Asset
                </span>
                <div class="d-flex align-items-center">
                    <div class="row">
                        <div class="badge mt-3 p-2 col">
                            <div class="">
                                <font-awesome-icon :icon="['fad', 'bars']" class="text-default" fixed-width />
                            </div>
                        </div>
                        <div v-if="asset && asset.previewpermlink" class="col pt-3">
                            <tippy placement="right" arrow interactive :delay="[500, 0]">
                                <template v-slot:trigger>
                                    <b-img
                                        :src="asset.previewpermlink"
                                        class="p-0"
                                        width="100"
                                        height="auto"
                                        thumbnail
                                    />
                                </template>
                                <div>
                                    <b-link :href="asset.previewpermlink" target="_blank">
                                        <b-img
                                            :src="asset.thumbpermlink"
                                            class="p-0"
                                            width="300"
                                            height="auto"
                                            thumbnail
                                        />
                                    </b-link>
                                </div>
                            </tippy>
                        </div>
                    </div>

                    <div class="flex-grow-1 mr-3">
                        <Title
                            v-model="worksheet.title"
                            :publication="publication"
                            :worksheet="worksheet"
                            :variant="'light'"
                            @update="updateWorksheetTitle"
                            :disabled="disabled"
                        />
                    </div>
                    <div class="ml-auto">
                        <b-dropdown
                            size="sm"
                            variant="secondary"
                            dropleft
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['fad', 'cog']" />
                            </template>
                            <b-dropdown-item v-if="worksheet.cdn_url" @click="copyCdnUrlToClipbord">
                                <font-awesome-icon :icon="['fad', 'copy']" class="text-primary" fixed-width />
                                Copy URL
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="editWorksheet(worksheet)"
                                :disabled="!can_update_printable || disabled"
                            >
                                <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width />
                                Edit
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="unlinkWorksheet"
                                :disabled="!can_remove_printable || disabled"
                            >
                                <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width
                                                   :disabled="!can_remove_printable" />
                                Unlink
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </template>

            <b-card-body v-if="worksheet_contents">
                <worksheet-contents :worksheet="worksheet" />
            </b-card-body>
        </b-card>

        <edit-printable-modal
            v-if="show_edit_printable_modal"
            v-model="show_edit_printable_modal"
            :editing_printable="worksheet"
            @hideUploadPrintable="show_edit_printable_modal = false"
            @getPrintables="$emit('getPrintables')"
        />

    </div>
</template>

<style lang="scss" scoped>
</style>

<script>
import {mapState} from 'vuex'
import CardID from '@/components/CardID'
import Title from '@/components/Title'
import WorksheetContents from '@/components/worksheet/WorksheetContents'
import EditPrintableModal from '@/components/modals/media/EditPrintableModal'
import draggable from 'vuedraggable'

export default {
    data: () => ({
        show_worksheet_modal: false,
        show_edit_printable_modal: false,
        asset: null,
    }),
    props: {
        'publication': Object,
        'worksheet': Object,
        'publication_worksheets': Array,
        'disabled': {
            type: Boolean,
            default: false,
        }
    },

    components: {
        CardID,
        Title,
        WorksheetContents,
        EditPrintableModal,
        draggable
    },
    computed: {
        ...mapState({
            user: state => state.user.user
        }),
        isMerlinMedia() {
            return this.asset && !this.worksheet.cdn_url
        },
        worksheet_contents() {
            let worksheet_contents = false

            if (
                this.worksheet.dam_id ||
                this.worksheet.worksheet_text ||
                this.worksheet.answer_key ||
                this.worksheet.student_directions ||
                this.worksheet.artist_description ||
                this.worksheet.artist_requirements ||
                this.worksheet.worksheet_suggestions
            ) {
                worksheet_contents = true
            }

            return worksheet_contents
        },
        can_remove_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_printable');
        },
        can_update_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_printable');
        }
    },
    methods: {
        editWorksheet(edit_printable) {
            this.$store.commit('setSelectedPrintable', Object.assign({}, edit_printable));
            this.show_edit_printable_modal = true;
        },
        updateWorksheet(worksheet) {
            if (
                worksheet &&
                worksheet.id
            ) {
                this.axios.post('/api/articles/update_worksheet/' + this.worksheet.id, worksheet)
                    .then(() => {
                        this.$emit('update')

                        this.show_worksheet_modal = false
                    })
            }
        },
        updateWorksheetByProperty(key, value) {
            return new Promise((resolve, reject) => {
                this.axios.post('/api/articles/update_worksheet/' + this.worksheet.id, {
                    [key]: value
                })
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
            })
        },
        updateWorksheetTitle(title) {
            this.updateWorksheetByProperty('title', title)
                .then(() => {
                    this.$set(this.worksheet, 'title', title)
                })
        },
        unlinkWorksheet() {
            this.$emit('unlink', this.worksheet)
        },
        copyCdnUrlToClipbord() {
            navigator.clipboard.writeText(this.worksheet.cdn_url);
        },
        getMerlinAsset() {
            let merlin_ids = []

            if (this.worksheet.dam_id) {
                merlin_ids.push(this.worksheet.dam_id)
            }

            if (this.worksheet.dam_id) {
                this.asset_loading = true

                this.axios.post('/api/merlin/get', {
                    merlin_ids: merlin_ids
                })
                    .then(response => {
                        _.each(response.data[0].assets, asset => {
                            if (asset.merlinid == this.worksheet.dam_id) this.asset = asset;
                        })
                        this.asset_loading = false
                    })
                    .catch(error => {
                        if (error) {
                            this.asset_loading = false
                        }
                    })
            }
        }
    },
    mounted() {
        this.getMerlinAsset();
    }
}
</script>
